import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import { logFirebaseConfig, checkFirebaseAuth } from "../utils/firebaseCheck";
import "../App.css";
import logo from "../assets/logo.png";

function RegistrationPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Check Firebase configuration on component mount
  useEffect(() => {
    logFirebaseConfig();
    checkFirebaseAuth().then(result => {
      if (!result.success) {
        console.warn("⚠️ Firebase Auth check:", result.message);
      } else {
        console.log("✅ Firebase Auth check:", result.message);
      }
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      
      // Store authentication token in localStorage
      const token = await userCredential.user.getIdToken();
      localStorage.setItem("token", token);
      
      // User successfully created
      // Navigate to dashboard after successful registration
      navigate("/dashboard", { replace: true });
    } catch (error) {
      // Handle Firebase errors
      let errorMessage = "Registration failed. Please try again.";
      
      switch (error.code) {
        case "auth/operation-not-allowed":
        case "auth/configuration-not-found":
          errorMessage = "Email/Password authentication is not enabled. Please enable it in Firebase Console. Check the browser console for more details.";
          console.error("❌ Firebase Auth Configuration Error:");
          console.error("1. Go to: https://console.firebase.google.com/");
          console.error(
            "2. Select project:",
            auth?.app?.options?.projectId || "(your Firebase project)"
          );
          console.error("3. Navigate to: Authentication > Sign-in method");
          console.error("4. Click on 'Email/Password'");
          console.error("5. Toggle 'Enable' to ON");
          console.error("6. Click 'Save'");
          logFirebaseConfig();
          break;
        case "auth/email-already-in-use":
          errorMessage = "This email is already registered. Please login instead.";
          break;
        case "auth/invalid-email":
          errorMessage = "Invalid email address.";
          break;
        case "auth/weak-password":
          errorMessage = "Password is too weak. Please choose a stronger password.";
          break;
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your connection.";
          break;
        default:
          errorMessage = error.message || "Registration failed. Please try again.";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <img src={logo} alt="Timely Logo" className="logo-image" />
        <h1 className="tagline">Smart Appointments</h1>
        <p className="sub-tagline">Wait on your own terms.</p>

        <h2 className="login-title">Create Account</h2>

        {error && (
          <div className="error-message" style={{ 
            color: "#d32f2f", 
            backgroundColor: "#ffebee", 
            padding: "10px", 
            borderRadius: "5px", 
            marginBottom: "15px",
            fontSize: "14px"
          }}>
            {error}
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="input-label">Email</label>
          <input
            type="email"
            className="login-input"
            placeholder="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />

          <label className="input-label">Password</label>
          <input
            type="password"
            className="login-input"
            placeholder="password (min. 6 characters)"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            minLength={6}
          />

          <label className="input-label">Confirm Password</label>
          <input
            type="password"
            className="login-input"
            placeholder="confirm password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            minLength={6}
          />

          <button 
            type="submit" 
            className="login-btn" 
            disabled={loading}
            style={{ opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer" }}
          >
            {loading ? "Creating Account..." : "Register"}
          </button>
        </form>

        <p className="remember-password" style={{ marginTop: "15px", fontSize: "14px" }}>
          Already have an account? <a href="/login" className="sign-in-link">Sign in</a>
        </p>
      </div>
    </div>
  );
}

export default RegistrationPage;

